import { Tooltip } from '@aecom-ai-engineering/react-components';
import { Menu } from '@ark-ui/react/menu';
import clsx from 'clsx';
import { Check, Plus, type LucideIcon } from 'lucide-react';
import { Fragment, useState, type FC, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { CONTEXT_KIND_ICONS } from './add-context/context-kind-icons';
import { useRfpContextKinds } from './add-context/use-rfp-context-kinds';
import { type AddContextMenuKind } from './types';
import './add-context-menu.css';

interface AddContextMenuProps {
  /**
   * The chat input row. The design lines the menu up with the input's left
   * border and sits it above the input's top edge, not the plus glyph's, so the
   * row is what the menu is positioned against. Measuring it on open also keeps
   * the menu right through the states that change the row's geometry - the
   * fullscreen variant's larger padding, and the drawer growing to 96px with
   * the trigger pinned to its bottom while a message is being composed.
   */
  anchorRef: RefObject<HTMLDivElement | null>;
  /**
   * The kind already attached to the next question, if any. The other kinds
   * are offered but not selectable: a question carries one kind of context.
   */
  attachedKind: AddContextMenuKind | null;
  /** How many of `attachedKind` are attached, shown on that kind's row. */
  attachedCount: number;
  /** Placement for the wrapper span, which is what sits in the input row. */
  className?: string;
  disabled: boolean;
  /** Reported so the composer can stop holding itself open once this closes. */
  onOpenChange?: (open: boolean) => void;
  onSelect: (kind: AddContextMenuKind) => void;
}

interface AddContextItem {
  icon: LucideIcon;
  kind: AddContextMenuKind;
  labelKey: string;
}

const ITEMS: AddContextItem[] = [
  {
    icon: CONTEXT_KIND_ICONS.document,
    kind: 'document',
    labelKey: 'prop-chat.add-context-document',
  },
  {
    icon: CONTEXT_KIND_ICONS.finding,
    kind: 'finding',
    labelKey: 'prop-chat.add-context-finding',
  },
];

// RFP Review only, and each only when the package has some.
const RFP_ITEMS: AddContextItem[] = [
  {
    icon: CONTEXT_KIND_ICONS.milestone,
    kind: 'milestone',
    labelKey: 'prop-chat.add-context-milestone',
  },
  {
    icon: CONTEXT_KIND_ICONS['response-requirement'],
    kind: 'response-requirement',
    labelKey: 'prop-chat.add-context-response-requirement',
  },
];

// The menu sits 8px above the input in the design: its bottom edge at y=840,
// the input's top edge at y=848.
const MENU_GUTTER = 8;

// The weight the design system draws its lucide icons at, rather than lucide's
// heavier default of 2. The design's glyphs are thin, and this is what the rest
// of the app asks for.
const ICON_STROKE_WIDTH = 1.75;

/*
 * `ds-body-font` rather than a `font-[var(...)]` class: Tailwind cannot tell
 * a family from a weight inside a bare `var()`, and compiles that shorthand to
 * `font-weight`, which drops the family on the floor. The rest of the chat gets
 * Switzer by inheriting this utility from the drawer, but the menu is portaled
 * to the body and sits outside that subtree, so it has to ask for it.
 */
const MINI_TYPOGRAPHY =
  'ds-body-font text-[length:var(--ds-typography-paragraph-mini-font-size)] font-[var(--ds-typography-paragraph-paragraph-weight)] leading-[var(--ds-typography-paragraph-mini-line-height)] tracking-[var(--ds-typography-paragraph-mini-letter-spacing)]';

export const AddContextMenu: FC<AddContextMenuProps> = ({
  anchorRef,
  attachedCount,
  attachedKind,
  className,
  disabled,
  onOpenChange,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  // The blocked row under the pointer, so only that row's tooltip opens. See its tooltip.
  const [hoveredBlockedKind, setHoveredBlockedKind] = useState<AddContextMenuKind | null>(null);
  const rfpKinds = useRfpContextKinds();
  const items = [...ITEMS, ...RFP_ITEMS.filter((item) => rfpKinds.includes(item.kind))];

  return (
    <Menu.Root
      positioning={{
        placement: 'top-start',
        gutter: MENU_GUTTER,
        // Fixed, not absolute: the menu renders inside the drawer (see below),
        // and the drawer clips its overflow.
        strategy: 'fixed',
        getAnchorRect: () => anchorRef.current?.getBoundingClientRect() ?? null,
      }}
      onSelect={({ value }) => onSelect(value as AddContextMenuKind)}
      onOpenChange={({ open }) => {
        setIsMenuOpen(open);
        onOpenChange?.(open);
      }}
    >
      {/*
       * The design system's Tooltip, which renders in place and takes its
       * surface from overlays/Tooltip.css, so it needs none of the `!important`
       * re-skinning the react-tooltip instances in prop-chat-message.tsx carry.
       * `shrink-0` because the wrapper span, not the button, is now the flex
       * child of the input row.
       */}
      <Tooltip
        className={clsx('add-context-tooltip shrink-0', className)}
        content={t('prop-chat.add-context')}
        side="top"
        /*
         * Held closed while the menu is open, or the tooltip sits over the menu
         * it just opened - clicking the trigger does not dismiss it on its own.
         * Ark still reports its own hover and focus through onOpenChange, so
         * mirroring that into state keeps both triggers working. Once the menu
         * closes the tooltip stays down until the pointer or focus arrives
         * again, which is what you want right after dismissing a menu.
         */
        open={isTooltipOpen && !isMenuOpen}
        onOpenChange={({ open }) => setIsTooltipOpen(open)}
      >
        <Menu.Trigger
          type="button"
          aria-label={t('prop-chat.add-context')}
          data-add-context=""
          disabled={disabled}
          // The glyph is 16px in the design, which is below a comfortable pointer
          // target, so the button keeps its drawn size and grows the hit area with
          // a pseudo-element instead. That leaves the layout untouched: the glyph
          // stays 16px, sitting where the input's own left padding puts it.
          className="add-context-menu-trigger relative flex size-[var(--ds-space-4)] shrink-0 items-center justify-center text-[var(--ds-color-bg-icon-secondary)] before:absolute before:inset-[calc(var(--ds-space-1-5)*-1)] before:content-[''] disabled:cursor-not-allowed disabled:text-[var(--ds-color-text-disabled)] enabled:cursor-pointer"
        >
          <Plus
            aria-hidden="true"
            strokeWidth={ICON_STROKE_WIDTH}
            className="size-[var(--ds-space-4)]"
          />
        </Menu.Trigger>
      </Tooltip>
      {/*
       * Rendered in place rather than through a Portal. Portaled to the body it
       * became a sibling of the drawer, whose positioner is `z-70` while a
       * positioner's own z-index is `var(--z-index)` - a custom property
       * nothing defines - so the menu opened, positioned correctly, and painted
       * behind the drawer's opaque background. Staying inside the drawer shares
       * its stacking context, so it needs no z-index of its own and no z-index
       * agreement with the drawer to keep working.
       */}
      <Menu.Positioner>
        <Menu.Content
          data-add-context=""
          className={`add-context-menu-surface overflow-hidden rounded-[var(--ds-radius-8)] border border-solid border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)] outline-none ${MINI_TYPOGRAPHY}`}
        >
          <Menu.ItemGroup className="add-context-menu-items">
            <Menu.ItemGroupLabel className="flex min-h-[var(--ds-space-8)] items-center px-[var(--ds-space-4)] text-[var(--ds-color-text-secondary)]">
              {t('prop-chat.add-context')}
            </Menu.ItemGroupLabel>
            {items.map(({ icon: Icon, kind, labelKey }) => {
              const blocked = attachedKind !== null && attachedKind !== kind;
              // Still pickable: it reopens the list with the attached rows ticked.
              const applied = attachedKind === kind;

              const item = (
                <Menu.Item
                  value={kind}
                  disabled={blocked}
                  // Ark sets data-highlighted on hover as well as on keyboard
                  // navigation, so the one rule covers both states the design
                  // draws, and it leaves a disabled row unhighlighted. A blocked
                  // row keeps its own colours - the cursor and the tooltip are
                  // what say it cannot be picked, not a greyed-out label. The last
                  // row's divider is dropped in the stylesheet rather than with
                  // `last:`, which a tooltip wrapper would defeat by making its
                  // item an only child.
                  className={clsx(
                    'flex min-h-[var(--ds-space-10)] cursor-pointer items-center gap-[var(--ds-space-1-5)] border-b border-solid border-[var(--ds-color-border-primary)] px-[var(--ds-space-4)] text-[var(--ds-color-text-primary)] outline-none data-[disabled]:cursor-not-allowed data-[highlighted]:bg-[var(--ds-color-bg-secondary-dark)]',
                    applied
                      ? 'bg-[var(--ds-color-bg-brand-light)]'
                      : 'bg-[var(--ds-color-bg-primary)]',
                  )}
                  data-applied={applied ? '' : undefined}
                >
                  <Icon
                    aria-hidden="true"
                    strokeWidth={ICON_STROKE_WIDTH}
                    className="size-[var(--ds-space-4)] shrink-0 text-[var(--ds-color-bg-icon-primary)]"
                  />
                  <Menu.ItemText className="min-w-0 flex-1 truncate">{t(labelKey)}</Menu.ItemText>
                  {applied && (
                    <span className="flex shrink-0 items-center gap-[var(--ds-space-1)] pl-[var(--ds-space-2)] text-[var(--ds-color-text-secondary)]">
                      {t('prop-chat.add-context-added', { count: attachedCount })}
                      <Check
                        aria-hidden="true"
                        strokeWidth={ICON_STROKE_WIDTH}
                        className="size-[var(--ds-space-4)] text-[var(--ds-color-bg-icon-primary)]"
                      />
                    </span>
                  )}
                </Menu.Item>
              );

              /*
               * The reason sits on the row it disables. Ark disables a menu item
               * with aria-disabled on a div rather than the disabled attribute,
               * so the row still takes pointer events and the tooltip opens on
               * it - which a genuinely disabled control would not do.
               */
              return blocked ? (
                <Tooltip
                  key={kind}
                  className="add-context-menu-item-tooltip"
                  content={t('prop-chat.add-context-one-type')}
                  side="top"
                  /*
                   * Held open by the pointer alone, rather than left to Ark,
                   * which dismisses a tooltip when its trigger is clicked. That
                   * default takes the explanation away at the exact moment it is
                   * wanted: clicking the row is how someone finds out it cannot
                   * be picked. So it closes on pointer-out and nothing else.
                   *
                   * Also gated on the menu, which the pointer can leave without
                   * crossing the row - dismissing with Escape, say - and would
                   * otherwise reopen this on the next visit with no hover.
                   */
                  open={hoveredBlockedKind === kind && isMenuOpen}
                  onPointerEnter={() => setHoveredBlockedKind(kind)}
                  // Only if still this row's: entering the next row can land before leaving this one.
                  onPointerLeave={() =>
                    setHoveredBlockedKind((current) => (current === kind ? null : current))
                  }
                >
                  {item}
                </Tooltip>
              ) : (
                <Fragment key={kind}>{item}</Fragment>
              );
            })}
          </Menu.ItemGroup>
        </Menu.Content>
      </Menu.Positioner>
    </Menu.Root>
  );
};
